import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Trophy, Youtube, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CustomHLSPlayer from '../components/CustomHLSPlayer';
import { getBuildInstruction, getBuildInstructionByUrl, DEFAULT_BUILD_INSTRUCTION } from '../data/buildInstructions';
import CompletionCelebration from '../components/CompletionCelebration';

interface BuildInstructionsProps {
    navHeight?: number;
    navMarginHeight?: number;
    productId?: string;
    productSlug?: string;
    version?: string;
}

const CIRCUMFERENCE = 2 * Math.PI * 30;

const BuildInstructions: React.FC<BuildInstructionsProps> = ({ navHeight = 0, navMarginHeight = 0, productId, productSlug, version }) => {
    const [isIntroduction, setIsIntroduction] = useState(true);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
    const [isMobileView, setIsMobileView] = useState(false);
    const [videoPlayFn, setVideoPlayFn] = useState<(() => Promise<void>) | null>(null);
    const [shouldAutoPlayNext, setShouldAutoPlayNext] = useState(false);
    const [hideModal, setHideModal] = useState(false);
    const [hideControlsOnAutoPlay, setHideControlsOnAutoPlay] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);
    const [buildStartTime, setBuildStartTime] = useState<number | null>(null);
    const [buildCompletedTime, setBuildCompletedTime] = useState<number | null>(null);

    const sidebarScrollRef = useRef<HTMLDivElement>(null);

    const buildInstruction = productSlug
        ? getBuildInstructionByUrl(productSlug, version)
        : getBuildInstruction(productId || DEFAULT_BUILD_INSTRUCTION);

    if (!buildInstruction) {
        throw new Error(`Build instructions not found for ${productSlug ? `${productSlug}/${version}` : productId || DEFAULT_BUILD_INSTRUCTION}`);
    }

    const currentStep = isIntroduction ? buildInstruction.introduction : buildInstruction.steps[currentStepIndex];
    const totalSteps = buildInstruction.steps.length;

    useEffect(() => {
        const checkMobileView = () => setIsMobileView(window.innerWidth < 768);
        checkMobileView();
        window.addEventListener('resize', checkMobileView);
        return () => window.removeEventListener('resize', checkMobileView);
    }, []);

    useEffect(() => {
        const storageKey = `kibu-build-completed-steps-${buildInstruction.id}`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                setCompletedSteps(new Set(JSON.parse(saved)));
            } catch {
                // ignore corrupt data
            }
        }
    }, [buildInstruction.id]);

    useEffect(() => {
        const storageKey = `kibu-build-completed-steps-${buildInstruction.id}`;
        localStorage.setItem(storageKey, JSON.stringify([...completedSteps]));
    }, [completedSteps, buildInstruction.id]);

    const goToStep = (index: number) => {
        setHideModal(true);
        setVideoPlayFn(null);
        if (index === -1) {
            setIsIntroduction(true);
            setCurrentStepIndex(0);
        } else if (index >= 0 && index < totalSteps) {
            if (buildStartTime === null) setBuildStartTime(Date.now());
            setIsIntroduction(false);
            setCurrentStepIndex(index);
        }
        setTimeout(() => setHideModal(false), 100);
    };

    const goToNextStep = () => {
        setHideModal(true);
        setVideoPlayFn(null);
        if (isIntroduction) {
            if (buildStartTime === null) setBuildStartTime(Date.now());
            setIsIntroduction(false);
            setCurrentStepIndex(0);
            setShouldAutoPlayNext(true);
        } else if (currentStepIndex < totalSteps - 1) {
            setCompletedSteps(prev => new Set([...prev, currentStep.id]));
            setCurrentStepIndex(currentStepIndex + 1);
            setShouldAutoPlayNext(true);
        } else {
            setCompletedSteps(prev => new Set([...prev, currentStep.id]));
            setBuildCompletedTime(Date.now());
            setShowCelebration(true);
        }
        setTimeout(() => setHideModal(false), 100);
    };

    const handleVideoReady = (playFn: () => Promise<void>) => {
        setVideoPlayFn(() => playFn);
        if (shouldAutoPlayNext) {
            playFn().catch(console.error);
            setShouldAutoPlayNext(false);
            setHideControlsOnAutoPlay(true);
            setTimeout(() => setHideControlsOnAutoPlay(false), 100);
        }
    };

    const goToPreviousStep = () => {
        setVideoPlayFn(null);
        if (!isIntroduction && currentStepIndex === 0) {
            setIsIntroduction(true);
        } else if (!isIntroduction && currentStepIndex > 0) {
            setCurrentStepIndex(currentStepIndex - 1);
        }
    };

    const hasNext = isIntroduction || currentStepIndex < totalSteps - 1;
    const hasPrevious = !isIntroduction && (currentStepIndex > 0 || (!isIntroduction && currentStepIndex === 0));
    const isOnFinalStep = !isIntroduction && currentStepIndex === totalSteps - 1;

    const handleManualCompletion = () => {
        setCompletedSteps(prev => new Set([...prev, currentStep.id]));
        setBuildCompletedTime(Date.now());
        setShowCelebration(true);
    };

    const handleBuildAnother = () => {
        setCompletedSteps(new Set());
        setIsIntroduction(true);
        setCurrentStepIndex(0);
        setBuildCompletedTime(null);
        setBuildStartTime(null);
        setShowCelebration(false);
        localStorage.removeItem(`kibu-build-completed-steps-${buildInstruction.id}`);
    };

    const scrollToCurrentStep = useCallback(() => {
        if (!sidebarScrollRef.current) return;
        const activeButton = isIntroduction
            ? sidebarScrollRef.current.querySelector('[data-step="introduction"]')
            : sidebarScrollRef.current.querySelector(`[data-step="step-${currentStepIndex}"]`);
        if (activeButton) {
            const container = sidebarScrollRef.current;
            const scrollToPosition = (activeButton as HTMLElement).offsetTop - container.clientHeight / 2 + (activeButton as HTMLElement).offsetHeight / 2;
            container.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
        }
    }, [isIntroduction, currentStepIndex]);

    useEffect(() => {
        const timer = setTimeout(scrollToCurrentStep, 100);
        return () => clearTimeout(timer);
    }, [isIntroduction, currentStepIndex, scrollToCurrentStep]);

    const progressPercentage = Math.round((completedSteps.size / totalSteps) * 100);
    const strokeDashoffset = CIRCUMFERENCE * (1 - progressPercentage / 100);

    const getBuildTime = () => {
        if (buildStartTime === null) return "0 seconds";
        const timeElapsed = (buildCompletedTime || Date.now()) - buildStartTime;
        const minutes = Math.floor(timeElapsed / 60000);
        const seconds = Math.floor((timeElapsed % 60000) / 1000);
        return minutes > 0 ? `${minutes} minute${minutes !== 1 ? 's' : ''}` : `${seconds} second${seconds !== 1 ? 's' : ''}`;
    };

    const stepKey = isIntroduction ? 'intro' : `step-${currentStepIndex}`;

    const youtubeLink = (
        <a
            href="https://www.youtube.com/watch?v=Mx3PJw8OKtI&t=52s"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-body text-gray-400 hover:text-primary transition-colors"
        >
            <Youtube className="w-4 h-4" />
            <span className="underline underline-offset-2">Watch on YouTube</span>
        </a>
    );

    if (isMobileView) {
        return (
            <div
                className="bg-background"
                style={{ marginTop: `${navMarginHeight}px`, minHeight: `calc(100vh - ${navMarginHeight}px)` }}
            >
                {/* Header */}
                <div className="bg-white shadow-sm border-b border-grey">
                    <div className="px-4 py-5">
                        <div className="flex items-center justify-between mb-4">
                            <h1 className="text-xl font-headline font-medium text-black">
                                Build Instructions
                            </h1>
                            <div className="text-sm font-body text-gray-500">
                                {isIntroduction ? 'Introduction' : `Step ${currentStepIndex + 1} of ${totalSteps}`}
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="w-full bg-grey rounded-full h-1.5">
                            <div
                                className="bg-orange h-1.5 rounded-full transition-all duration-500"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>
                        <div className="text-xs font-body text-gray-400 mt-1.5">
                            {completedSteps.size} of {totalSteps} steps complete
                        </div>
                    </div>

                    {/* Step pills */}
                    <div className="px-4 pb-4">
                        <div className="flex gap-2 overflow-x-auto no-scrollbar">
                            <button
                                onClick={() => goToStep(-1)}
                                className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-body transition-all ${
                                    isIntroduction
                                        ? 'bg-orange text-white shadow-sm'
                                        : 'bg-white text-gray-600 hover:bg-grey border border-grey'
                                }`}
                            >
                                Intro
                            </button>
                            {buildInstruction.steps.map((step, index) => {
                                const isActive = !isIntroduction && index === currentStepIndex;
                                const isComplete = completedSteps.has(step.id);
                                return (
                                    <button
                                        key={step.id}
                                        onClick={() => goToStep(index)}
                                        className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-body transition-all flex items-center gap-1 ${
                                            isActive
                                                ? 'bg-orange text-white shadow-sm'
                                                : isComplete
                                                    ? 'bg-green/10 text-green border border-green/30'
                                                    : 'bg-white text-gray-600 hover:bg-grey border border-grey'
                                        }`}
                                    >
                                        {isComplete && <Check className="w-3 h-3" strokeWidth={2.5} />}
                                        {index + 1}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Animated content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={stepKey}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -12 }}
                        transition={{ duration: 0.22, ease: [0.25, 0.25, 0, 1] }}
                    >
                        {/* Video */}
                        <div className="px-4 pt-4">
                            <CustomHLSPlayer
                                src={currentStep.videoUrl}
                                title={currentStep.title}
                                onNext={hasNext ? goToNextStep : undefined}
                                onPrevious={hasPrevious ? goToPreviousStep : undefined}
                                hasNext={hasNext}
                                hasPrevious={hasPrevious}
                                className="aspect-square w-full"
                                onVideoReady={handleVideoReady}
                                hideNextStepModal={hideModal}
                                hideControlsOnAutoPlay={hideControlsOnAutoPlay}
                                disableFullscreen={true}
                                hideBottomControls={true}
                                showTimeInTopLeft={true}
                            />
                        </div>

                        {/* Navigation */}
                        <div className="px-4 py-3">
                            <div className="flex justify-between gap-3">
                                <button
                                    onClick={goToPreviousStep}
                                    disabled={!hasPrevious}
                                    className="flex items-center gap-1.5 px-4 py-2.5 border-2 border-grey text-gray-600 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:border-gray-400 transition-colors font-body text-sm"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </button>
                                <button
                                    onClick={isOnFinalStep ? handleManualCompletion : goToNextStep}
                                    disabled={!hasNext && !isOnFinalStep}
                                    className="flex items-center gap-1.5 px-4 py-2.5 bg-orange text-white rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-orange/90 transition-all hover:shadow-button-hover font-body text-sm font-medium"
                                >
                                    {isIntroduction ? 'Start Building' : isOnFinalStep ? 'Complete Build!' : 'Next'}
                                    {isOnFinalStep ? <Trophy className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Step info */}
                        <div className="px-4 pb-6">
                            <div className="bg-white rounded-2xl p-5 shadow-sm">
                                <h2 className="font-headline font-medium text-black mb-3">
                                    {currentStep.title}
                                </h2>
                                <p className="text-gray-600 font-body leading-relaxed mb-4">
                                    {currentStep.description}
                                </p>
                                <div className="border-t border-grey pt-4">
                                    <p className="text-xs text-gray-400 font-body mb-2">Need help with this step?</p>
                                    {youtubeLink}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>

                {showCelebration && (
                    <CompletionCelebration
                        totalSteps={totalSteps}
                        buildTime={getBuildTime()}
                        productName={buildInstruction.productName || "Headphones"}
                        onClose={() => setShowCelebration(false)}
                        onBuildAnother={handleBuildAnother}
                    />
                )}
            </div>
        );
    }

    // Desktop layout
    return (
        <div
            className="bg-background relative"
            style={{ marginTop: `${navMarginHeight}px`, minHeight: `calc(100vh - ${navMarginHeight}px)` }}
        >
            {/* Sidebar */}
            <div
                className="absolute left-0 top-0 w-80 bg-white shadow-lg border-r border-grey flex flex-col z-30"
                style={{ height: '100%' }}
            >
                {/* Sidebar header with circular progress */}
                <div className="flex-shrink-0 p-6 border-b border-grey">
                    <h1 className="text-xl font-headline font-medium text-black mb-5">
                        Build Instructions
                    </h1>

                    <div className="flex items-center gap-4">
                        {/* Circular progress ring */}
                        <div className="relative flex-shrink-0">
                            <svg width="72" height="72" viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
                                <circle cx="40" cy="40" r="30" fill="none" stroke="#E9E9E9" strokeWidth="8" />
                                <circle
                                    cx="40" cy="40" r="30"
                                    fill="none"
                                    stroke="#FF784D"
                                    strokeWidth="8"
                                    strokeLinecap="round"
                                    strokeDasharray={CIRCUMFERENCE}
                                    strokeDashoffset={strokeDashoffset}
                                    style={{ transition: 'stroke-dashoffset 0.6s cubic-bezier(0.25, 0.25, 0, 1)' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-sm font-headline font-medium text-black">
                                    {progressPercentage}%
                                </span>
                            </div>
                        </div>
                        <div>
                            <div className="text-sm font-headline text-black">
                                {completedSteps.size} / {totalSteps}
                            </div>
                            <div className="text-xs font-body text-gray-400 mt-0.5">steps complete</div>
                        </div>
                    </div>
                </div>

                {/* Step list */}
                <div ref={sidebarScrollRef} className="flex-1 overflow-y-auto min-h-0 pb-24">
                    <div className="p-4">
                        <div className="space-y-1.5">
                            {/* Introduction */}
                            <button
                                onClick={() => goToStep(-1)}
                                data-step="introduction"
                                className={`w-full text-left p-4 rounded-xl transition-all duration-200 ${
                                    isIntroduction
                                        ? 'bg-orange/10 border-2 border-orange'
                                        : 'bg-background hover:bg-grey border-2 border-transparent'
                                }`}
                            >
                                <div className="flex items-center space-x-3">
                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-body ${
                                        isIntroduction ? 'border-orange text-orange' : 'border-gray-300 text-gray-400'
                                    }`}>
                                        i
                                    </div>
                                    <div className="flex-grow">
                                        <div className="font-body font-medium text-black text-sm mb-0.5">
                                            {buildInstruction.introduction.title}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {buildInstruction.introduction.duration}
                                        </div>
                                    </div>
                                </div>
                            </button>

                            {buildInstruction.steps.map((step, index) => {
                                const isActive = !isIntroduction && index === currentStepIndex;
                                const isComplete = completedSteps.has(step.id);
                                return (
                                    <button
                                        key={step.id}
                                        onClick={() => goToStep(index)}
                                        data-step={`step-${index}`}
                                        className={`w-full text-left p-4 rounded-xl transition-all duration-200 ${
                                            isActive
                                                ? 'bg-orange/10 border-2 border-orange'
                                                : isComplete
                                                    ? 'bg-green/10 border-2 border-green/20'
                                                    : 'bg-background hover:bg-grey border-2 border-transparent'
                                        }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="flex-shrink-0">
                                                <AnimatePresence mode="wait">
                                                    {isComplete ? (
                                                        <motion.div
                                                            key="complete"
                                                            initial={{ scale: 0, rotate: -90 }}
                                                            animate={{ scale: 1, rotate: 0 }}
                                                            transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                                                            className="w-6 h-6 rounded-full bg-green flex items-center justify-center"
                                                        >
                                                            <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                                                        </motion.div>
                                                    ) : (
                                                        <motion.div
                                                            key="pending"
                                                            initial={{ scale: 0.8 }}
                                                            animate={{ scale: 1 }}
                                                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-body ${
                                                                isActive
                                                                    ? 'border-orange text-orange'
                                                                    : 'border-gray-300 text-gray-400'
                                                            }`}
                                                        >
                                                            {index + 1}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                            <div className="flex-grow">
                                                <div className={`font-body font-medium text-sm mb-0.5 ${
                                                    isComplete ? 'text-green' : 'text-black'
                                                }`}>
                                                    {step.title}
                                                </div>
                                                {step.duration && (
                                                    <div className="text-xs text-gray-400">{step.duration}</div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col ml-80 min-h-screen">
                {/* Step header */}
                <div className="bg-white shadow-sm border-b border-grey px-8 py-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-headline font-medium text-black">
                                {currentStep.title}
                            </h2>
                            <p className="text-gray-400 font-body mt-0.5 text-sm">
                                {isIntroduction ? 'Introduction' : `Step ${currentStepIndex + 1} of ${totalSteps}`}
                            </p>
                        </div>
                        {isOnFinalStep && (
                            <button
                                onClick={handleManualCompletion}
                                className="flex items-center gap-2 px-4 py-2 bg-orange text-white font-body font-medium rounded-full hover:bg-orange/90 transition-all hover:shadow-button-hover text-sm"
                            >
                                <Trophy className="w-4 h-4" />
                                Complete Build!
                            </button>
                        )}
                    </div>
                </div>

                {/* Animated step content */}
                <div className="flex-1 p-8 overflow-y-auto">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={stepKey}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.25, ease: [0.25, 0.25, 0, 1] }}
                            className="max-w-4xl mx-auto"
                        >
                            <CustomHLSPlayer
                                src={currentStep.videoUrl}
                                title={currentStep.title}
                                onNext={hasNext ? goToNextStep : undefined}
                                onPrevious={hasPrevious ? goToPreviousStep : undefined}
                                hasNext={hasNext}
                                hasPrevious={hasPrevious}
                                className="aspect-video w-full mb-2"
                                onVideoReady={handleVideoReady}
                                hideNextStepModal={hideModal}
                                hideControlsOnAutoPlay={hideControlsOnAutoPlay}
                            />

                            {/* Navigation */}
                            <div className="flex justify-between items-center my-6">
                                <button
                                    onClick={goToPreviousStep}
                                    disabled={!hasPrevious}
                                    className="flex items-center gap-2 px-6 py-3 border-2 border-grey text-gray-600 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:border-gray-400 transition-colors font-body"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                    Previous Step
                                </button>
                                <button
                                    onClick={isOnFinalStep ? handleManualCompletion : goToNextStep}
                                    disabled={!hasNext && !isOnFinalStep}
                                    className="flex items-center gap-2 px-6 py-3 bg-orange text-white rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-orange/90 transition-all hover:shadow-button-hover font-body font-medium"
                                >
                                    {isIntroduction ? 'Start Building' : isOnFinalStep ? 'Complete Build!' : 'Next Step'}
                                    {isOnFinalStep ? <Trophy className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                                </button>
                            </div>

                            {/* Step description */}
                            <div className="bg-white rounded-xl p-6 shadow-sm">
                                <p className="text-gray-700 font-body leading-relaxed text-lg mb-6">
                                    {currentStep.description}
                                </p>
                                <div className="border-t border-grey pt-5">
                                    <p className="text-sm text-gray-400 font-body mb-2">
                                        Need help with this step?
                                    </p>
                                    {youtubeLink}
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {showCelebration && (
                <CompletionCelebration
                    totalSteps={totalSteps}
                    buildTime={getBuildTime()}
                    productName={buildInstruction.productName || "Headphones"}
                    onClose={() => setShowCelebration(false)}
                    onBuildAnother={handleBuildAnother}
                />
            )}
        </div>
    );
};

export default BuildInstructions;
